import React, { Component } from "react";
import { Undo2, AlertTriangle } from "lucide-react";
import { Dialog } from "primereact/dialog";
import { toastService } from "../../services/toastService";
import { PaymentStatus, TransactionReportDetails, TransactionRequestModel, TransactionStatus } from "../../models/Transactions";
import moment from "moment";
import { AppCard, LoadingDots } from "../../components/ui";
import { InputText } from "primereact/inputtext";
import Loader from "../../components/Loader";
import { transactionsReportApiService } from "../../services/api/operations/TransactionsReportApiService";
import ReturnTransactionDetailsDialog from "./ReturnTransactionDetails";
import { utils } from "../../utils/utils";
import { RollbackDepositRequestModel } from "../../models/RollbackDepositRequestModel";
import { individualPaymentApiService } from "../../services/api/individual/IndividualPaymentApiService";

import { Button } from "primereact/button";
import { ConfirmDialog } from "primereact/confirmdialog";
import { ProgressSpinner } from 'primereact/progressspinner';
import { isRollbackTransactionAllowed } from "../../authGuard";
import { Toast } from "primereact/toast";

interface TransactionDetailsDialogProps {
    isVisible: boolean;
    onHide: () => void;
    transaction: TransactionReportDetails | any;
    editable: boolean;
    onRollbackSuccess?: () => void | Promise<void>;
}

interface TransactionDetailsDialogState {
    isDetailVisible: boolean;
    transaction: TransactionReportDetails | any;
    viewTransactionDetails: boolean;
    selectedTransaction: TransactionReportDetails | null;
    isLoadingSpinnerVisible: boolean;
    isBlockingOverlayVisible: boolean;
    isConfirmDialogVisible: boolean;
}

export default class TransactionDetailsDialog extends Component<TransactionDetailsDialogProps, TransactionDetailsDialogState> {
    private toast: Toast | null = null;

    constructor(props: TransactionDetailsDialogProps | Readonly<TransactionDetailsDialogProps>) {
        super(props);

        this.state = {
            isDetailVisible: false,
            transaction: this.props.transaction,
            viewTransactionDetails: false,
            selectedTransaction: null,
            isLoadingSpinnerVisible: false,
            isBlockingOverlayVisible: false,
            isConfirmDialogVisible: false,
        };
    }


    componentDidMount() {
        this.checkTransactionReversal();
    }

    componentDidUpdate(prevProps: TransactionDetailsDialogProps) {
        // When parent gives a new transaction, re-check
        if (prevProps.transaction?.transactionId !== this.props.transaction?.transactionId) {
            this.setState(
                { transaction: this.props.transaction },
                () => this.checkTransactionReversal()
            );
        }
    }

    toggleAdvancedMode = () => {
        this.setState({ isDetailVisible: !this.state.isDetailVisible });
    };

    getTrasaction = async (id: string) => {
        this.setState({ isDetailVisible: true });
        const request: TransactionRequestModel = {
            transactionId: id,
        };
        const transactionResult = await transactionsReportApiService.getTransactionById(request);
        if (!transactionResult.success) {
            this.setState({ isDetailVisible: false });
            toastService.error(transactionResult.message);
            return;
        }

        this.setState({ isDetailVisible: false, selectedTransaction: transactionResult.data });
    };

    renderTransactionDetailsRow = (label: string, value: string | number | any) => {
        return (
            <div className="flex justify-between items-center p-[0.2rem] text-[0.9rem]">
                <span className="text-[#495057] font-bold">{utils.toPascalCase(label)}</span>
                <span className="text-[#666] font-normal text-right">{value}</span>
            </div>
        );
    };

    renderTransactionDetails = () => {
        return Object.entries(this.state.transaction).map(([key, value]) => {
            if (key === "timeStamp" && typeof value === "string") {
                value = moment.utc(value).local().format("DD/MM/YYYY HH:mm:ss");
            }

            return this.renderTransactionDetailsRow(key, value);
        });
    };

    // Focus the close button on dialog startup, as the Revert button becomes the default focus after its addition.
    private focusDialogCloseButton = () => {
        setTimeout(() => {
            const closeButton = document.querySelector("[data-pc-section='closebutton']") as HTMLButtonElement;
            if (closeButton) {
                closeButton.focus();
            } else {
                console.warn("Close button of Dialog not found. Check if the selector is correct.");
            }
        }, 100); // Delay to ensure the dialog is rendered
    };

    private canReverseTransaction = (): boolean => {
        const tx = this.state.transaction;
        return !!(tx?.isCardDeposit && tx?.canBeReversed && Number(tx?.amount) > 0);
    };

    private reverseTransactionTooltipMessage = (): string | undefined => {
        // Button is disabled if loading, API not ready, or transaction cannot be reversed
        const isDisabled =
            this.state.isLoadingSpinnerVisible ||
            this.state.transaction?.canBeReversed === undefined ||
            !this.canReverseTransaction();

        if (!isDisabled) {
            return undefined; // No tooltip when button is enabled
        }

        if (this.state.isLoadingSpinnerVisible || this.state.transaction?.canBeReversed === undefined) {
            return "Checking reversal status…";
        }

        return "This transaction has already been reversed or is not eligible for reversal";
    };

  // Call this when the dialog opens or when props.transaction changes
    checkTransactionReversal = async (skipSpinner = false) => {
        const txId = this.state.transaction?.transactionId;

        if (!txId) {
            return;
        }

        if (!skipSpinner) {
            this.setState({ isLoadingSpinnerVisible: true });
        }

        try {
            const result = await individualPaymentApiService.isTransactionReversed(txId);
            const isReversed = result?.data?.isReversed ?? false;

            this.setState(prev => {
                const newState: Partial<TransactionDetailsDialogState> = {
                    transaction: {
                        ...prev.transaction,
                        canBeReversed: !isReversed
                    }
                };

                if (!skipSpinner) {
                    newState.isLoadingSpinnerVisible = false;
                }

                return newState as Pick<TransactionDetailsDialogState, "transaction" | "isLoadingSpinnerVisible">;
            });
        } catch (err) {
            console.error("checkTransactionReversal failed", err);
            if (!skipSpinner) {
                this.setState({ isLoadingSpinnerVisible: false });
            }
        }
    };

    handleRollbackDeposit = async () => {
        try {
            this.setState({ isLoadingSpinnerVisible: true });

            const requestModel: RollbackDepositRequestModel = {
                flexCubeTransactionReference: this.state.transaction.transactionId
            };
            const response = await individualPaymentApiService.rollbackDeposit(requestModel);

            const toastMessageDefaultLifeInMs = 3000;

            if (!response.success) {
                this.toast?.show({ severity: 'error', summary: 'Error', detail: response.message, life: toastMessageDefaultLifeInMs });
                return;
            }

            // The inner try-catch ensures the success toast and dialog close always happen even if the refresh fails — since at that point the rollback itself already succeeded.
            try {
                // Refresh FIRST while spinner is still showing
                await this.refreshTransactionAfterRollback();

                if (this.props.onRollbackSuccess) {
                    await this.props.onRollbackSuccess();
                }
            } catch (err) {
                console.error("Refresh after rollback failed", err);
            }

            // Hide spinner, show overlay, THEN show toast so user sees it for full duration
            this.setState({ isBlockingOverlayVisible: true, isLoadingSpinnerVisible: false });
            this.toast?.show({ severity: 'success', summary: 'Success', detail: response.message, life: toastMessageDefaultLifeInMs });

            setTimeout(() => {
                this.setState({ isBlockingOverlayVisible: false });
                this.props.onHide();
            }, toastMessageDefaultLifeInMs);

        } catch (error) {
            console.error("Error reverse deposit:", error);
            this.toast?.show({ severity: 'error', summary: 'Error', detail: 'Unexpected error occurred while reversing transaction.' });
        } finally {
            if (this.state.isLoadingSpinnerVisible) {
                this.setState({ isLoadingSpinnerVisible: false });
            }
        }
    };

    confirmRollback = () => {
        this.setState({ isConfirmDialogVisible: true });
    };

    private refreshTransactionAfterRollback = async () => {
        const transactionId = this.state.transaction?.transactionId;

        if (!transactionId) return;

        const request: TransactionRequestModel = { transactionId };
        const transactionResult = await transactionsReportApiService.getTransactionById(request);

        if (transactionResult.success) {
            this.setState(prev => ({
                transaction: {
                    ...transactionResult.data,
                    canBeReversed: prev.transaction?.canBeReversed ?? false
                }
            }));
        }

        await this.checkTransactionReversal(true);
    };

    /**
     * Immutably updates a single field on the transaction state.
     * Replaces direct mutation patterns (e.g. transaction.amount = val) with a safe shallow-copy approach to ensure React detects the change.
    **/
    private updateTransactionField = (field: string, value: any) => {
        this.setState(prev => {
            if (!prev.transaction) return null;

            return {
                transaction: {
                    ...prev.transaction,
                    [field]: value
                }
            };
        });
    };

    private renderRollbackConfirmDialog = () => {
        return (
            <ConfirmDialog
                visible={this.state.isConfirmDialogVisible}
                onHide={() => this.setState({ isConfirmDialogVisible: false })}
                message="Are you sure you want to reverse this transaction?"
                header="Confirmation"
                icon="pi pi-exclamation-triangle"
                rejectLabel="Cancel"
                acceptLabel="Reverse"
                accept={() => {
                    this.setState(
                        { isConfirmDialogVisible: false },
                        () => {
                            setTimeout(() => this.handleRollbackDeposit(), 100);
                        }
                    );
                }}
                reject={() => this.setState({ isConfirmDialogVisible: false })}
                acceptClassName="accept-rollback-deposit-button"
                className="custom-confirm-dialog"
                defaultFocus="reject"
            />
        );
    };

    navigateToTransaction = async (id: string) => {
        const request: TransactionRequestModel = { transactionId: id };
        const transactionResult = await transactionsReportApiService.getTransactionById(request);
        
        if (!transactionResult.success) {
            this.toast?.show({ severity: "error", summary: "Error", detail: transactionResult.message, life: 3000 });
            return;
        }

        this.setState(
            { transaction: transactionResult.data },
            () => this.checkTransactionReversal()
        );
    };

   render() {
    if (this.state.transaction == null) {
        return "";
    }

    if (!this.props.isVisible) {
        return "";
    } else {
        this.focusDialogCloseButton();
    }

    return (
        <>
            {this.renderRollbackConfirmDialog()}

            <Dialog
                header={
                    <div className="flex items-center justify-between w-full">
                        <span>Transaction Details</span>

                        {isRollbackTransactionAllowed() && this.state.transaction.isCardDeposit && Number(this.state.transaction.amount) > 0 && (
                            <div>
                                <Button
                                    label="Reverse transaction"
                                    icon={<Undo2 size={16} />}
                                    text
                                    severity="danger"
                                    onClick={this.confirmRollback}
                                    disabled={
                                        this.state.isLoadingSpinnerVisible ||
                                        this.state.transaction?.canBeReversed === undefined ||
                                        !this.canReverseTransaction()
                                    }
                                    tooltip={this.reverseTransactionTooltipMessage()}
                                    tooltipOptions={{ showOnDisabled: true, position: "bottom" }}
                                />
                            </div>
                        )}
                    </div>
                }
                visible={this.props.isVisible}
                style={{ width: "70vw" }}
                onHide={this.props.onHide}
                closable={true}
                closeOnEscape={true}
            >
                <div style={{ maxHeight: "70vh", overflowY: "auto", padding: "10px" }}>

                    {this.state.isBlockingOverlayVisible && (
                        <div
                            style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                backgroundColor: "rgba(0,0,0,0.3)",
                                zIndex: 1000,
                                cursor: "not-allowed",
                            }}
                        />
                    )}

                    {this.state.isLoadingSpinnerVisible && (
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center z-[9998]">
                            <ProgressSpinner className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                        </div>
                    )}

                    <Toast ref={(el) => (this.toast = el)} position="bottom-center" baseZIndex={99999} />

                    {this.state.isDetailVisible && (
                        <Dialog closable={false} visible={this.state.isDetailVisible} onHide={() => {}}>
                            <LoadingDots />
                        </Dialog>
                    )}

                    {this.state.selectedTransaction != null && (
                        <ReturnTransactionDetailsDialog
                            onHide={() => this.setState({ isDetailVisible: false, selectedTransaction: null })}
                            transaction={this.state.selectedTransaction}
                        />
                    )}

                    <div className="space-y-4">
                        <AppCard><AppCard.Header title="Transaction Summary" /><AppCard.Body>
                            {renderDualRow(
                                "Transaction Id",
                                this.state.transaction.transactionId,
                                false,
                                () => {},
                                "Type",
                                this.state.transaction.transactionType,
                                false,
                                () => {},
                            )}
                            {renderDualRow(
                                "Timestamp",
                                moment.utc(this.state.transaction.timeStamp).local().format("DD/MM/YYYY HH:mm:ss"),
                                false,
                                () => {},
                                "Amount",
                                this.state.transaction.amount > 0 ? this.state.transaction.amount : "-",
                                this.props.editable,
                                (newVal: any) => this.updateTransactionField("amount", newVal),
                            )}
                            {renderDualRow(
                                "Transaction Fee",
                                this.state.transaction.amount > 0 ? this.state.transaction.transactionFee : "-",
                                this.props.editable,
                                (newVal: any) => this.updateTransactionField("transactionFee", newVal),
                                "Currency",
                                this.state.transaction.currency,
                                false,
                                () => {},
                            )}
                            {renderDualRow(
                                "Status",
                                this.state.transaction.transactionStatus,
                                false,
                                () => {},
                                "Transaction Category",
                                this.state.transaction.transactionCategory,
                                this.props.editable,
                                (newVal: any) => this.updateTransactionField("transactionCategory", newVal),
                            )}

                                        {this.state.transaction.wireTransferRelatedTransId
                                            ? renderDualRow(
                                                  "Return Transaction Id",
                                                  this.state.transaction.wireTransferRelatedTransId,
                                                  false,
                                                  () => {},
                                                  null,
                                                  "",
                                                  false,
                                                  () => {},
                                                  true,
                                                  () => {
                                                      this.getTrasaction(this.state.transaction.wireTransferRelatedTransId);
                                                  },
                                              )
                                            : this.state.transaction.rejectedTransctionRef && this.state.transaction.rejectedTransctionStatus
                                              ? renderDualRow(
                                                    "Rejected Transaction Id",
                                                    this.state.transaction.rejectedTransctionRef,
                                                    false,
                                                    () => {},
                                                    null,
                                                    "",
                                                    false,
                                                    () => {},
                                                    true,
                                                    () => {
                                                        this.getTrasaction(this.state.transaction.rejectedTransctionRef);
                                                    },
                                                )
                                              : this.state.transaction.wireTransferReason
                                                ? renderDualRow(
                                                      "Reason",
                                                      this.state.transaction.wireTransferReason,
                                                      false,
                                                      () => {},
                                                      null,
                                                      "",
                                                      false,
                                                      () => {},
                                                  )
                                                : ""}
                                        {this.state.transaction.originalFlexCubeReference &&
                                            renderDualRow(
                                                "Original Transaction Reference",
                                                this.state.transaction.originalFlexCubeReference,
                                                false,
                                                () => {},
                                                null,
                                                "",
                                                false,
                                                () => {},
                                                true,
                                                () => {
                                                    this.navigateToTransaction(this.state.transaction.originalFlexCubeReference);
                                                },
                                            )}
                        </AppCard.Body></AppCard>

                        <AppCard><AppCard.Header title="Sender Info" /><AppCard.Body>
                            {renderDualRow(
                                "Name",
                                this.state.transaction.senderName,
                                this.props.editable,
                                (newVal: string) => this.updateTransactionField("senderName", newVal),
                                "Merchant",
                                this.state.transaction.merchant,
                                false,
                                () => {},
                            )}
                            {renderDualRow(
                                "Source",
                                this.state.transaction.senderIban,
                                this.props.editable,
                                (newVal: string) => this.updateTransactionField("senderIban", newVal),
                                "Payment Notes",
                                this.state.transaction.paymentNotes,
                                false,
                                () => {},
                            )}
                        </AppCard.Body></AppCard>

                        <AppCard><AppCard.Header title="Recepient Info" /><AppCard.Body>
                            {renderDualRow(
                                "Name",
                                this.state.transaction.receiverName,
                                this.props.editable,
                                (newVal: string) => this.updateTransactionField("receiverName", newVal),
                            )}
                            {renderDualRow(
                                "Destination",
                                this.state.transaction.receiverIban,
                                this.props.editable,
                                (newVal: string) => this.updateTransactionField("receiverIban", newVal),
                            )}
                        </AppCard.Body></AppCard>

                        <AppCard><AppCard.Header title="Advanced Info" /><AppCard.Body>
                            {renderDualRow(
                                "Sender Authorization Token",
                                this.state.transaction.senderAuthData,
                                false,
                                () => {},
                                "Sender Authorization Data",
                                this.state.transaction.senderAuthData,
                                false,
                                () => {},
                            )}
                            {renderDualRow(
                                "Sender Ip",
                                this.state.transaction.senderIp,
                                false,
                                () => {},
                                "Transaction Duration",
                                this.state.transaction.duration,
                                false,
                                () => {},
                            )}
                            {renderDualRow(
                                "CBS Transaction Id",
                                this.state.transaction.cbsTransactionId,
                                false,
                                () => {},
                                "CBS Details",
                                this.state.transaction.cbsDetails,
                                false,
                                () => {},
                            )}
                        </AppCard.Body></AppCard>

                        <AppCard><AppCard.Header title="Transaction Details" /><AppCard.Body>
                            <div className="w-full flex flex-col gap-4">{this.renderTransactionDetails()}</div>
                        </AppCard.Body></AppCard>
                    </div>
                </div>
            </Dialog>
        </>
    );
}
}

export function renderDualRow(
    title1: string,
    val1: string | number,
    editable1: boolean,
    onEdit1: (newval: string) => void,
    title2: string | null = null,
    val2: string | number = null as any,
    editable2: boolean = null as any,
    onEdit2: (newval: string) => void = null as any,
    link?: boolean,
    linkFun?: () => void,
) {
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-1">
            <div className="grid grid-cols-2 gap-2 items-center">
                <span className="text-sm font-bold text-neutral-700 dark:text-neutral-300">
                    {title1} :
                </span>
                <div>
                    {editable1 ? (
                        <InputText type="text" value={String(val1)} onChange={(e) => onEdit1(e.target.value)} />
                    ) : link ? (
                        <a
                            href="#"
                            className="text-blue-600 underline"
                            onClick={(e) => {
                                e.preventDefault();
                                linkFun!();
                            }}
                        >
                            {val1}
                        </a>
                    ) : (
                        <span className="text-sm text-neutral-600 dark:text-neutral-400">{val1}</span>
                    )}
                </div>
            </div>
            {title2 != null && (
                <div className="grid grid-cols-2 gap-2 items-center">
                    <span className="text-sm font-bold text-neutral-700 dark:text-neutral-300">
                        {title2} :
                    </span>
                    <div>
                        {editable2 ? (
                            <InputText type="text" value={String(val2)} onChange={(e) => onEdit2(e.target.value)} />
                        ) : (
                            <span className="text-sm text-neutral-600 dark:text-neutral-400">{val2}</span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
